<template>
    <section class="space-y-5 rounded-3xl border border-primary-medium bg-primary-light p-4 shadow-2xl ring-1 ring-primary-medium/20 sm:p-6">
        <div class="overflow-hidden rounded-3xl border border-primary-medium bg-white shadow-lg">
            <div class="bg-gradient-to-r from-dark via-primary-dark to-primary-dark px-4 py-3 text-primary-lighter sm:px-5 sm:py-4">
                <div class="flex flex-col gap-2">
                    <div class="space-y-1.5">
                        <span
                            class="inline-flex rounded-full border border-primary-lighter/15 bg-white/10 px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.22em] text-primary-light uppercase"
                        >
                            {{ t('reservation.step5.summary') }}
                        </span>
                        <h3 class="text-lg font-bold tracking-tight sm:text-xl">
                            {{ t('reservation.step5.title') }}
                        </h3>
                        <p class="max-w-2xl text-xs leading-5 text-primary-light/80 sm:text-sm sm:leading-6">
                            {{ t('reservation.step5.reviewDetails') }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Top overview -->
            <div class="space-y-3 border-b border-primary-medium/70 bg-primary-light/70 p-4 sm:p-5">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p class="text-xs font-semibold tracking-[0.18em] text-primary-dark/70 uppercase">
                            {{ t('reservation.step5.trip') }}
                        </p>
                        <p class="mt-1 text-base font-semibold text-dark">
                            {{ t(`reservation.step1.${tripType === 'oneway' ? 'oneWay' : 'roundTrip'}`) }}
                            <span v-if="tripType === 'oneway'" class="text-dark/60">— {{ t(`reservation.step1.${onewayDirection}`) }}</span>
                        </p>
                    </div>

                    <span class="rounded-full border border-primary-medium bg-white px-3 py-1.5 text-sm font-medium text-dark shadow-sm">
                        {{ t('reservation.step5.fromTo') }}: {{ t(`locations.${from}`) }} → {{ t(`locations.${dropTo}`) }}
                    </span>
                </div>

                <p v-if="hotel" class="flex flex-wrap items-center gap-2 text-sm text-dark">
                    <span
                        class="inline-flex rounded-full bg-primary-dark px-3 py-1 text-xs font-semibold tracking-[0.18em] text-primary-light uppercase"
                    >
                        {{ t('reservation.step1.hotels.title') }}
                    </span>
                    <span>{{ hotel }}</span>
                </p>
            </div>

            <!-- Details grid -->
            <div class="grid grid-cols-1 gap-4 p-4 sm:p-5 lg:grid-cols-2">
                <!-- Passengers & equipment -->
                <div class="rounded-2xl border border-primary-medium/70 bg-white p-4 shadow-sm">
                    <h4 class="mb-4 text-xs font-semibold tracking-[0.18em] text-primary-dark/70 uppercase">
                        {{ t('reservation.step5.passengers') }}
                    </h4>

                    <div class="space-y-3">
                        <div class="flex flex-wrap items-center gap-2 text-sm text-dark">
                            <span class="rounded-full bg-primary-light px-3 py-1">{{ adults }} {{ t('reservation.step1.adults') }}</span>
                            <span class="rounded-full bg-primary-light px-3 py-1">{{ children }} {{ t('reservation.step1.children') }}</span>
                            <span class="rounded-full bg-primary-light px-3 py-1">{{ babies }} {{ t('reservation.step1.babies') }}</span>
                        </div>

                        <div class="flex items-center justify-between rounded-xl bg-primary-dark px-4 py-3 text-primary-light">
                            <span class="text-sm font-medium">{{ t('reservation.step1.total') }}</span>
                            <span class="text-lg font-bold text-primary-lighter">{{ totalPassengers }}</span>
                        </div>

                        <div class="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                            <div class="rounded-xl border border-primary-medium/60 bg-primary-light px-3 py-2">
                                <p class="font-medium text-dark">{{ t('reservation.step1.babySeat') }}</p>
                                <p class="mt-1 text-base font-bold text-primary-dark">{{ babySeat }}</p>
                            </div>
                            <div class="rounded-xl border border-primary-medium/60 bg-primary-light px-3 py-2">
                                <p class="font-medium text-dark">{{ t('reservation.step1.childSeat') }}</p>
                                <p class="mt-1 text-base font-bold text-primary-dark">{{ childSeat }}</p>
                            </div>
                            <div class="rounded-xl border border-primary-medium/60 bg-primary-light px-3 py-2">
                                <p class="font-medium text-dark">{{ t('reservation.step1.numberOfStrollers') }}</p>
                                <p class="mt-1 text-base font-bold text-primary-dark">{{ numberOfStrollers }}</p>
                            </div>
                            <div class="rounded-xl border border-primary-medium/60 bg-primary-light px-3 py-2">
                                <p class="font-medium text-dark">{{ t('reservation.step1.numberOfSuitcases') }}</p>
                                <p class="mt-1 text-base font-bold text-primary-dark">{{ numberOfSuitcases }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Dates & times -->
                <div class="rounded-2xl border border-primary-medium/70 bg-white p-4 shadow-sm">
                    <h4 class="mb-4 text-xs font-semibold tracking-[0.18em] text-primary-dark/70 uppercase">
                        {{ t('reservation.step1.date') }} / {{ t('reservation.step1.time') }}
                    </h4>

                    <div v-if="tripType === 'oneway'" class="space-y-3 text-sm">
                        <div class="flex items-center justify-between rounded-xl bg-primary-light px-4 py-3">
                            <span class="text-dark/70">{{ t('reservation.step1.date') }}</span>
                            <span class="font-semibold text-dark">{{ formattedDateOneWay }}</span>
                        </div>
                        <div class="flex items-center justify-between rounded-xl bg-primary-light px-4 py-3">
                            <span class="text-dark/70">{{ t('reservation.step1.time') }}</span>
                            <span class="font-semibold text-dark">{{ formatTime(timeOneWayStr) }}</span>
                        </div>
                        <div
                            v-if="onewayDirection === 'departure'"
                            class="flex items-center justify-between rounded-xl border border-primary-medium/70 bg-primary-dark px-4 py-3 text-primary-light"
                        >
                            <span>{{ t('reservation.step5.pickUp') }}</span>
                            <span class="font-semibold text-primary-lighter">{{ formatTime(pickUpTimeOneWay) }}</span>
                        </div>
                    </div>

                    <div v-else class="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                        <div class="rounded-xl border border-primary-medium/70 bg-primary-light px-4 py-3">
                            <p class="text-xs font-semibold tracking-[0.14em] text-primary-dark/60 uppercase">{{ t('reservation.step5.arrival') }}</p>
                            <p class="mt-2 font-semibold text-dark">{{ formattedDateArrival }}</p>
                            <p class="text-dark/70">{{ formatTime(timeArrivalStr) }}</p>
                        </div>
                        <div class="rounded-xl border border-primary-medium/70 bg-primary-light px-4 py-3">
                            <p class="text-xs font-semibold tracking-[0.14em] text-primary-dark/60 uppercase">
                                {{ t('reservation.step5.departure') }}
                            </p>
                            <p class="mt-2 font-semibold text-dark">{{ formattedDateDeparture }}</p>
                            <p class="text-dark/70">{{ formatTime(timeDepartureStr) }}</p>
                            <p class="mt-2 text-xs text-dark/70">
                                <span class="font-medium text-dark">{{ t('reservation.step5.pickUp') }}:</span> {{ formatTime(pickUpTimeDeparture) }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Travel reference -->
                <div class="rounded-2xl border border-primary-medium/70 bg-white p-4 shadow-sm">
                    <h4 class="mb-4 text-xs font-semibold tracking-[0.18em] text-primary-dark/70 uppercase">
                        {{ t('reservation.step5.travel') }}
                    </h4>
                    <div class="space-y-3 text-sm">
                        <div class="rounded-xl bg-primary-light px-4 py-3">
                            <p class="text-xs font-semibold tracking-[0.14em] text-primary-dark/60 uppercase">
                                {{ t('reservation.step2.travelType') }}
                            </p>
                            <p class="mt-1 font-semibold text-dark">{{ travelType ? t(`reservation.step2.${travelType}`) : '—' }}</p>
                            <p v-if="travelNumber" class="mt-1 text-dark/70">{{ travelNumber }}</p>
                        </div>
                        <div class="rounded-xl bg-primary-light px-4 py-3">
                            <p class="text-xs font-semibold tracking-[0.14em] text-primary-dark/60 uppercase">
                                {{ t('reservation.step2.vehicleType') }}
                            </p>
                            <p class="mt-1 font-semibold text-dark">{{ vehicleType || '—' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Client -->
                <div class="rounded-2xl border border-primary-medium/70 bg-white p-4 shadow-sm">
                    <h4 class="mb-4 text-xs font-semibold tracking-[0.18em] text-primary-dark/70 uppercase">
                        {{ t('reservation.step5.client') }}
                    </h4>
                    <div class="space-y-2 text-sm text-dark">
                        <p class="font-semibold">{{ client.name }}</p>
                        <p class="text-dark/75">{{ client.email }}</p>
                        <p class="text-dark/75">{{ client.phone }}</p>
                        <p
                            v-if="client.notes"
                            class="rounded-xl border border-dashed border-primary-medium/70 bg-primary-light px-4 py-3 text-sm leading-6 text-dark/80 italic"
                        >
                            {{ client.notes }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="space-y-3 rounded-2xl border border-primary-medium/70 bg-white p-4 shadow-sm">
            <label class="flex items-start gap-3 text-sm text-dark">
                <input
                    type="checkbox"
                    class="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    :checked="privacyAccepted"
                    @change="emit('update:privacyAccepted', ($event.target as HTMLInputElement).checked)"
                />
                <span>
                    {{ t('reservation.step5.privacyConsentPrefix') }}
                    <a href="/politique-confidentialite" class="font-medium text-primary underline">{{ t('reservation.step5.privacyPolicyLink') }}</a
                    >{{ t('reservation.step5.privacyConsentSuffix') }}
                </span>
            </label>
            <p v-if="!privacyAccepted" class="text-xs font-medium text-red-600">
                {{ t('reservation.step5.privacyConsentRequired') }}
            </p>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-end">
            <button type="button" @click="$emit('submit')" :disabled="!privacyAccepted" class="btn btn-primary btn-disabled-state">
                {{ t('reservation.step5.finishReservation') }}
            </button>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useDateFormatter } from '@/composables/useDateFormatter';
import { useTranslations } from '@/composables/useTranslations';
import { computed } from 'vue';

const { t } = useTranslations();
const { formatDate, formatTime } = useDateFormatter();

interface ClientInfo {
    name: string;
    email: string;
    phone: string;
    notes: string;
}

interface Props {
    tripType: 'oneway' | 'roundtrip';
    onewayDirection: 'arrival' | 'departure';
    from: string;
    dropTo: string;
    hotel: string;
    adults: number;
    children: number;
    babies: number;
    babySeat: number;
    childSeat: number;
    numberOfStrollers: number;
    numberOfSuitcases: number;
    totalPassengers: number;
    dateOneWay: string;
    dateArrival: string;
    dateDeparture: string;
    timeOneWayStr: string;
    timeArrivalStr: string;
    timeDepartureStr: string;
    pickUpTimeOneWay: string;
    pickUpTimeDeparture: string;
    travelType: 'flight' | 'train' | '';
    travelNumber: string;
    vehicleType: string;
    client: ClientInfo;
    privacyAccepted: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (e: 'submit'): void;
    (e: 'update:privacyAccepted', value: boolean): void;
}>();

const formattedDateOneWay = computed(() => formatDate(props.dateOneWay));
const formattedDateArrival = computed(() => formatDate(props.dateArrival));
const formattedDateDeparture = computed(() => formatDate(props.dateDeparture));
</script>
